import React, { useState, useEffect, useRef } from 'react'
import { useMusicPlayer } from './MusicPlayerContext.jsx'
import { useOverlayManager } from './overlayManager.jsx'
import './unifiedUI.scss'

/**
 * OverlayNavigation Component
 * 
 * Navigation UI overlay for gallery overlays
 * Includes:
 * - Hamburger menu with overlay navigation
 * - Breadcrumb navigation (Windows Explorer style)
 * - Sound button with music/audio controls
 */

const OverlayNavigation = ({
  breadcrumbPath,
  onBreadcrumbClick,
  currentItemIndex,
  maxItems,
  onClose,
  audioEnabled,
  setAudioEnabled,
  annotationsEnabled = true,
  setAnnotationsEnabled,
  currentLevel
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isSoundOpen, setIsSoundOpen] = useState(false)
  const menuTimeoutRef = useRef(null)
  const soundTimeoutRef = useRef(null)
  
  const { showOverlay } = useOverlayManager()
  const { 
    isPlaying, 
    togglePlayPause, 
    currentTrack, 
    nextTrack, 
    previousTrack,
    setMusicEnabled: setGlobalMusicEnabled,
    musicEnabled: globalMusicEnabled
  } = useMusicPlayer()
  
  const [musicEnabled, setMusicEnabled] = useState(true)

  // Handle music toggle
  const handleMusicToggle = () => {
    const newMusicState = !musicEnabled
    setMusicEnabled(newMusicState)
    setGlobalMusicEnabled(newMusicState)
    
    if (!newMusicState) {
      console.log('Music manually disabled by user via overlay navigation')
    } else {
      console.log('Music manually enabled by user via overlay navigation')
    }
    resetSoundTimeout()
  }

  // Handle audio toggle (sound effects)
  const handleAudioToggle = () => {
    const newAudioState = !audioEnabled
    setAudioEnabled(newAudioState)
    console.log('Audio toggle via overlay navigation:', newAudioState)
    resetSoundTimeout()
  }

  // Check if we're on mobile and at level 2 or deeper
  const isMobile = window.innerWidth <= 768
  const showBackArrow = isMobile && breadcrumbPath && breadcrumbPath.length >= 2

  // Handle back button click - navigate up one level
  const handleBackClick = () => {
    if (breadcrumbPath && breadcrumbPath.length >= 2) {
      const previousLevel = breadcrumbPath[breadcrumbPath.length - 2].level
      onBreadcrumbClick && onBreadcrumbClick(previousLevel)
    }
  }

  // Auto-close menus after 5 seconds
  useEffect(() => {
    if (isMenuOpen) {
      menuTimeoutRef.current = setTimeout(() => setIsMenuOpen(false), 5000)
    } else if (menuTimeoutRef.current) {
      clearTimeout(menuTimeoutRef.current)
    }
    return () => menuTimeoutRef.current && clearTimeout(menuTimeoutRef.current)
  }, [isMenuOpen])

  useEffect(() => {
    if (isSoundOpen) {
      soundTimeoutRef.current = setTimeout(() => setIsSoundOpen(false), 5000)
    } else if (soundTimeoutRef.current) {
      clearTimeout(soundTimeoutRef.current)
    }
    return () => soundTimeoutRef.current && clearTimeout(soundTimeoutRef.current)
  }, [isSoundOpen])

  const resetMenuTimeout = () => {
    if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current)
    if (isMenuOpen) {
      menuTimeoutRef.current = setTimeout(() => setIsMenuOpen(false), 5000)
    }
  }

  const resetSoundTimeout = () => {
    if (soundTimeoutRef.current) clearTimeout(soundTimeoutRef.current)
    if (isSoundOpen) {
      soundTimeoutRef.current = setTimeout(() => setIsSoundOpen(false), 5000)
    }
  }

  const toggleMenu = () => {
    if (!isMenuOpen) setIsSoundOpen(false) // Close sound if opening menu
    setIsMenuOpen(!isMenuOpen)
  }

  const toggleSound = () => {
    if (!isSoundOpen) setIsMenuOpen(false) // Close menu if opening sound
    setIsSoundOpen(!isSoundOpen)
  }

  const handleNavigation = (overlay) => {
    resetMenuTimeout()
    setIsMenuOpen(false)
    
    if (overlay === 'resume') {
      window.open('/documents/resume-bjoernGrauers-2025.pdf', '_blank')
      return
    }
    
    showOverlay(overlay)
  }

  return (
    <>
      <div className='nav-container'>
        <div className='wrapper-nav-top'>
          {/* Left side: hamburger button */}
          <div className="btn-set">
            <button className="nav-btn" onClick={toggleMenu}>
              <span className="material-symbols-rounded">menu</span>
            </button>
            
            {/* Hamburger Menu Dropdown */}
            {isMenuOpen && (
              <div 
                className="nav-dropdown"
                onMouseEnter={resetMenuTimeout}
                onMouseLeave={resetMenuTimeout}
              >
                <button className="mobile-close-btn" onClick={() => setIsMenuOpen(false)}>✕</button>
                
                <div className="dropdown-item nav-item" onClick={() => handleNavigation('work')}>
                  <span>Work</span>
                </div>
                
                <div className="dropdown-item nav-item" onClick={() => handleNavigation('about')}>
                  <span>About</span>
                </div>
                
                <div className="dropdown-item nav-item" onClick={() => handleNavigation('music')}>
                  <span>Music</span>
                </div>
                
                <div className="dropdown-item nav-item" onClick={() => handleNavigation('contact')}>
                  <span>Contact</span>
                </div>

                <div className="dropdown-item nav-item" onClick={() => handleNavigation('resume')}>
                  <span>Resume</span>
                </div>
              </div>
            )}
          </div>
        
          {/* Back arrow on mobile (level 2+) */}
          {showBackArrow && (
            <button className="nav-btn nav-back-btn" onClick={handleBackClick}>
              <span className="material-symbols-rounded">arrow_back</span>
            </button>
          )}
          
          {/* Center: breadcrumb navigation */}
          {breadcrumbPath && breadcrumbPath.length > 0 && (
            <div className="nav-breadcrumb">
              {breadcrumbPath.map((crumb, index) => (
                <React.Fragment key={index}>
                  {/* Skip first item if back arrow is shown */}
                  {!(showBackArrow && index === 0) && (
                    <button
                      className={`menu-item ${index === breadcrumbPath.length - 1 ? 'focus' : ''}`}
                      onClick={() => onBreadcrumbClick && onBreadcrumbClick(crumb.level)}
                    >
                      {crumb.label}
                    </button>
                  )}
                  {/* Show "..." before first visible item when back arrow is shown, otherwise "/" */}
                  {index < breadcrumbPath.length - 1 && (
                    <span className="menu-separator">
                      {showBackArrow && index === 0 ? '...' : '/'}
                    </span>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {/* Right side: annotation, sound, and close buttons */}
        <div className="nav-button-set">
          {/* Annotation toggle - only show at level 3 */}
          {currentLevel === 3 && (
            <button 
              className="nav-btn canvas-nav-annotations" 
              onClick={() => setAnnotationsEnabled(!annotationsEnabled)}
              title={annotationsEnabled ? 'Hide annotations' : 'Show annotations'}
            >
              <span className="material-symbols-outlined">
                {annotationsEnabled ? 'comment' : 'comments_disabled'}
              </span>
            </button>
          )}
          <button className="nav-btn canvas-nav-sound" onClick={toggleSound}>
            <span className="material-symbols-rounded">volume_up</span>
          </button>
          <button className="nav-btn canvas-nav-close" onClick={onClose}>
            <span className="material-symbols-rounded">close</span>
          </button>
          
        </div>
        
        {/* Sound Dropdown */}
        {isSoundOpen && (
          <div 
            className="nav-dropdown nav-dropdown-right"
            onMouseEnter={resetSoundTimeout}
            onMouseLeave={resetSoundTimeout}
          >
            <button className="mobile-close-btn" onClick={() => setIsSoundOpen(false)}>✕</button>
              
              {/* Music Player Section */}
              {musicEnabled && currentTrack && (
                <>
                  <div className="dropdown-item">
                    <div className="text-subheading-2" style={{ textAlign: 'center', marginBottom: 'var(--spacing-medium)' }}>
                      {currentTrack.title}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 'var(--spacing-small)' }}>
                      <button className="btn" onClick={() => { resetSoundTimeout(); previousTrack(); }}>
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/>
                        </svg>
                      </button>
                      <button className="btn" onClick={() => { resetSoundTimeout(); togglePlayPause(); }}>
                        {isPlaying ? (
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        )}
                      </button>
                      <button className="btn" onClick={() => { resetSoundTimeout(); nextTrack(); }}>
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </>
              )}
              
              <div className="dropdown-item">
                <div className="toggle-row" onClick={handleMusicToggle}>
                  <span>Music</span>
                  <button className={`btn ${musicEnabled ? 'active' : ''}`}>
                    {musicEnabled ? (
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                        <line x1="3" y1="3" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              
              <div className="dropdown-item">
                <div className="toggle-row" onClick={handleAudioToggle}>
                  <span>Audio</span>
                  <button className={`btn ${audioEnabled ? 'active' : ''}`}>
                    {audioEnabled ? (
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3 10v4c0 .55.45 1 1 1h3l3.29 3.29c.63.63 1.71.18 1.71-.71V6.41c0-.89-1.08-1.34-1.71-.71L7 9H4c-.55 0-1 .45-1 1zm13.5 2c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3.63 3.63c-.39.39-.39 1.02 0 1.41L7.29 8.7 7 9H4c-.55 0-1 .45-1 1v4c0 .55.45 1 1 1h3l3.29 3.29c.63.63 1.71.18 1.71-.71v-4.17l4.18 4.18c-.49.37-1.02.68-1.6.91-.36.15-.58.53-.58.92 0 .72.73 1.18 1.39.91.8-.33 1.55-.77 2.22-1.31l4.18 4.18c.39.39 1.02.39 1.41 0 .39-.39.39-1.02 0-1.41L5.05 3.63c-.39-.39-1.02-.39-1.42 0z"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
      </div>
    </>
  )
}

export default OverlayNavigation
